import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from '../BoardColumn/index.module.scss';
import { IWorkspaceStatus } from 'src/models/workspace/WorkspaceStatus/types';
import classNames from 'classnames';
import { useClickAway } from 'react-use';
import { notification, confirm, Tooltip } from 'src/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { QuickActions, QuickActionsDivider, QuickActionsItem, EditableText } from 'src/shared';
import { faBarsStaggered, faPen, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { WORKSPACE_STATUS_CATEGORY_ICON_MAPPER } from 'src/models/workspace/WorkspaceStatus/mappers';
import modalStyles from 'src/common/Modal/index.module.scss';
import {
  getUpdateCacheOnCreateWorkspaceStatus,
  getUpdateCacheOnDeleteWorkspaceStatus,
  useCreateWorkspaceStatus,
  useDeleteWorkspaceStatus,
  useUpdateWorkspaceStatus,
  useSortedWorkspaceStatuses
} from 'src/models/workspace/WorkspaceStatus/hooks';
import { WorkspaceStatusCategory } from '@tendium/prom-types/tender';
import {
  geneRankToLeft,
  geneRankToRight,
  isStatusNameExisting,
  toNewStatusName,
  toStatusName
} from 'src/models/workspace/WorkspaceStatus/helpers';
import { IBoxFieldString } from 'src/models/procurements/Tender/types';
import { useNewStatusIdContext, useSetNewStatusIdContext } from '../hooks';
import { useFeatureFlag, FeatureFlag } from 'src/helpers/featureFlag';
import { useWorkspace } from 'src/models/workspace/hooks';
import { BoxFieldString } from 'src/models/procurements';
import { useTotalStagesBids } from 'src/models/bids/Bids/hooks';
import useBidsSearchArgs from 'src/reactiveVars/BidsSearchArgsVar';
import { trackAddStageToWorkspace, trackDeleteStageFromWorkspace } from 'src/segment/events';

interface IProps {
  status: IWorkspaceStatus | null;
  bidsCount: number;
  onToggleClick: () => void;
}

export const BoardHeader: FC<IProps> = props => {
  const { status, onToggleClick, bidsCount } = props;

  const [getTotalBids] = useTotalStagesBids();

  const { data: wsData } = useWorkspace();
  const wsId = wsData?.id;
  const accessToEdit = wsData?.accessToEdit;

  const allStatuses = useSortedWorkspaceStatuses();
  const newStatusId = useNewStatusIdContext();
  const setNewStatusId = useSetNewStatusIdContext();
  const isBidStagesEditable = useFeatureFlag(FeatureFlag.BidStages);

  const { t } = useTranslation();
  const statusName = useMemo(() => {
    return toStatusName(status?.name, t);
  }, [status, t]);

  const statusId = useMemo(() => {
    return status?.id || null;
  }, [status]);

  const statusCategory = useMemo(() => {
    return status?.category || WorkspaceStatusCategory.Interesting;
  }, [status]);

  const statusIcon = useMemo(() => {
    return WORKSPACE_STATUS_CATEGORY_ICON_MAPPER[statusCategory] || '';
  }, [statusCategory]);

  const statuses = useMemo(
    () => allStatuses.filter(item => item.category === statusCategory),
    [allStatuses, statusCategory]
  );

  const statusesCount = useMemo(() => statuses.length, [statuses]);

  const [deleteWorkspaceStatus, { loading: isDeleting }] = useDeleteWorkspaceStatus();
  const [createWorkspaceStatus] = useCreateWorkspaceStatus();
  const [updateWorkspaceStatus] = useUpdateWorkspaceStatus();

  const [isEdit, setIsEdit] = useState(!!newStatusId && newStatusId === statusId);

  const onToggleClickCB = useCallback(() => {
    !isEdit && onToggleClick();
  }, [isEdit, onToggleClick]);

  const onError = useCallback(() => {
    notification.error({
      description: t('Common.unknownErrorDesc'),
      message: t('Common.unknownError')
    });
  }, [t]);

  const onNameExisting = useCallback(
    name => {
      confirm.confirm({
        centered: true,
        className: modalStyles.confirmModal,
        title: t('BidSpaces.newStage'),
        content: <Trans i18nKey={'BidSpaces.stageExistingMessage'} values={{ name }} />,
        okText: t('Common.ok'),
        cancelButtonProps: {
          hidden: true
        }
      });
    },
    [t]
  );

  const onRenameStage = useCallback(() => setIsEdit(true), []);

  const onSaveRenamedStage = useCallback(
    (val: IBoxFieldString) => {
      setIsEdit(false);
      const newName = (val.string || '').trim();
      if (newName === statusName) {
        return;
      }
      if (isStatusNameExisting(allStatuses, newName)) {
        return onNameExisting(newName);
      }
      statusId && updateWorkspaceStatus({ variables: { id: statusId, name: newName } }).catch(onError);
    },
    [allStatuses, onError, onNameExisting, statusId, statusName, updateWorkspaceStatus]
  );

  const [args] = useBidsSearchArgs();

  const onDeleteStage = useCallback(() => {
    if (!statusId || !wsId) {
      return;
    }
    getTotalBids({
      variables: {
        wsId: wsId || '',
        stageIds: [statusId],
        ...args,
        amount: 10
      }
    })
      .then(data => {
        const totalBidsCount = data.data?.getBids.totalBids;

        if (statusesCount < 2) {
          confirm.confirm({
            centered: true,
            className: modalStyles.confirmModal,
            title: t('BidSpaces.deleteStage'),
            content: t('BidSpaces.deleteStageWithLastStage'),
            okText: t('ok'),
            cancelButtonProps: {
              hidden: true
            }
          });
          return;
        }
        if (!!totalBidsCount) {
          confirm.confirm({
            centered: true,
            className: modalStyles.confirmModal,
            title: t('BidSpaces.deleteStage'),
            content: <Trans i18nKey={'BidSpaces.deleteStageWithBids'} values={{ count: totalBidsCount }} />,
            okText: t('BidSpaces.deleteAndMove'),
            okButtonProps: {
              disabled: true
            }
          });
          return;
        }
        confirm.confirm({
          centered: true,
          className: modalStyles.confirmModal,
          title: t('BidSpaces.deleteStage'),
          content: t('BidSpaces.deleteStageConfirm'),
          okText: t('Common.delete'),
          okButtonProps: {
            disabled: isDeleting
          },
          cancelText: t('Common.cancel'),
          onOk() {
            deleteWorkspaceStatus({
              variables: { id: statusId },
              update: getUpdateCacheOnDeleteWorkspaceStatus(statusId, wsId),
              optimisticResponse: {
                __typename: 'Mutation',
                deleteWorkspaceStatus: true
              }
            })
              .then(() => {
                trackDeleteStageFromWorkspace(
                  {
                    id: wsId
                  },
                  statusCategory
                );
              })
              .catch(onError);
          }
        });
      })
      .catch(() => {
        notification.warn({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
  }, [
    statusId,
    wsId,
    getTotalBids,
    args,
    statusesCount,
    t,
    isDeleting,
    deleteWorkspaceStatus,
    onError,
    statusCategory
  ]);

  const onAddStage = useCallback(
    dir => {
      if (!wsId || !statusId) {
        return;
      }
      const name = toNewStatusName(allStatuses, t('BidSpaces.newStage'));

      if (isStatusNameExisting(allStatuses, name)) {
        return onNameExisting(name);
      }

      const rank = dir > 0 ? geneRankToRight(statuses, statusId) : geneRankToLeft(statuses, statusId);
      rank &&
        createWorkspaceStatus({
          variables: { workspaceId: wsId, name, rank, category: statusCategory },
          update: getUpdateCacheOnCreateWorkspaceStatus(wsId, id => setNewStatusId(id))
        })
          .then(() => {
            trackAddStageToWorkspace(
              {
                id: wsId,
                name: name
              },
              statusCategory
            );
          })
          .catch(onError);
    },
    [
      wsId,
      statusId,
      allStatuses,
      t,
      statuses,
      createWorkspaceStatus,
      statusCategory,
      onError,
      onNameExisting,
      setNewStatusId
    ]
  );

  const onAddStageToLeft = useCallback(() => {
    onAddStage(-1);
  }, [onAddStage]);

  const onAddStageToRight = useCallback(() => {
    onAddStage(1);
  }, [onAddStage]);

  const ref = useRef<HTMLDivElement>(null);

  useClickAway(ref, e => {
    e.stopPropagation();
    e.preventDefault();
    setIsEdit(false);
  });

  return (
    <>
      <div className={classNames(styles.statusHeader)} onClick={onToggleClickCB}>
        <h4 className={classNames(styles.statusTitle)}>
          {statusIcon && (
            <FontAwesomeIcon
              icon={statusIcon.defaultIcon}
              className={styles.statusIcon}
              style={{ color: statusIcon.color }}
            />
          )}
          {isEdit && statusId ? (
            <EditableText
              className={styles.editableName}
              containerRef={ref}
              onChange={onSaveRenamedStage}
              field={new BoxFieldString(statusName, statusName)}
              autoSize={{ minRows: 1, maxRows: 1 }}
              autoFocus
            />
          ) : (
            <>
              <Tooltip title={statusName}>
                <span className={styles.statusName}>{statusName}</span>
              </Tooltip>
              <span className={styles.statusQty}>{bidsCount}</span>
            </>
          )}
        </h4>
        <FontAwesomeIcon icon={faChevronRight} className={styles.toggleIcon} />
        {isBidStagesEditable && accessToEdit && (
          <QuickActions buttonClassName={styles.statusActionsButton}>
            <QuickActionsItem title={t(`BidSpaces.WorkspaceStatusCategory.${statusCategory}`)} readonly />
            <QuickActionsDivider />
            <QuickActionsItem
              key={'renameStage'}
              onClick={onRenameStage}
              title={t('BidSpaces.renameStage')}
              icon={faPen}
            />
            <QuickActionsItem
              key={'addStageToLeft'}
              onClick={onAddStageToLeft}
              title={t('BidSpaces.addStageToLeft')}
              icon={faBarsStaggered}
            />
            <QuickActionsItem
              key={'addStageToRight'}
              onClick={onAddStageToRight}
              title={t('BidSpaces.addStageToRight')}
              icon={faBarsStaggered}
            />
            <QuickActionsDivider />
            <QuickActionsItem
              key={'deleteGroup'}
              onClick={onDeleteStage}
              title={t('BidSpaces.deleteStage')}
              icon={faTrashCan}
              danger
            />
          </QuickActions>
        )}
      </div>
    </>
  );
};
export default BoardHeader;
